// src/components/FutureRAFScore.jsx
import React, { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend } from 'recharts';

const FUTURE_COLORS = ['#4caf50', '#81c784', '#a5d6a7', '#c5e1a5', '#e6ee9c'];

const getCode = (item) =>
  item?.code || item?.hcc || item?.icd10 || '';

const normalizeConditions = (conditions) => {
  if (!Array.isArray(conditions)) return [];

  return conditions
    .map((c) => {
      const w = Number(c.rafWeight);
      if (!Number.isFinite(w) || w <= 0) return null;

      return {
        code: getCode(c) || 'HCC',
        weight: w,
      };
    })
    .filter(Boolean);
};

const FutureRAFScore = ({
  currentConditions = [],
  futureConditions = [],
}) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const {
    currentData,
    futureData,
    totalCurrent,
    totalFuture,
    delta,
  } = useMemo(() => {
    // 1) RAF actual (misma lógica que RAFScore)
    const currentNorm = normalizeConditions(currentConditions);
    const totalCurrent = currentNorm.reduce(
      (sum, e) => sum + e.weight,
      0
    );

    // 2) RAF futuro: usamos futureConditions,
    //    y si viene vacío, caemos a currentConditions.
    const baseFuture =
      Array.isArray(futureConditions) && futureConditions.length > 0
        ? futureConditions
        : currentConditions;

    // Evitar duplicar códigos si IA envía las mismas condiciones en ambos
    const seenCodes = new Set();
    const dedupFuture = baseFuture.filter((c) => {
      const code = getCode(c);
      if (!code) return false;
      if (seenCodes.has(code)) return false;
      seenCodes.add(code);
      return true;
    });

    const futureNorm = normalizeConditions(dedupFuture);
    const totalFuture = futureNorm.reduce(
      (sum, e) => sum + e.weight,
      0
    );

    const delta = totalFuture - totalCurrent;

    return {
      currentData: currentNorm,
      futureData: futureNorm,
      totalCurrent,
      totalFuture,
      delta,
    };
  }, [currentConditions, futureConditions]);

  const hasFutureData = futureData.length > 0 && totalFuture > 0;

  return (
    <>
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="Future RAF Score"
          subheader="Projected score if AI-suggested HCC codes are fully captured"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon fontSize="large" color="success" />
              <Tooltip title="View assumptions for this projection">
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  sx={{
                    bgcolor: 'warning.light',
                    '&:hover': { bgcolor: 'warning.main' },
                    color: 'common.black',
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {!hasFutureData ? (
            <Typography variant="body2">
              Run &quot;Generate HCC with AI&quot; to see the projected RAF score.
            </Typography>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <PieChartIcon sx={{ mr: 1, color: 'success.main' }} />
                Projected RAF Breakdown by Condition
              </Typography>

              <PieChart width={400} height={300}>
                <Pie
                  data={futureData}
                  dataKey="weight"
                  nameKey="code"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => {
                    const percent =
                      totalFuture > 0
                        ? ((entry.weight / totalFuture) * 100).toFixed(0)
                        : 0;
                    return `${entry.code}: ${percent}%`;
                  }}
                >
                  {futureData.map((entry, index) => (
                    <Cell
                      key={`future-cell-${index}`}
                      fill={FUTURE_COLORS[index % FUTURE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value) => {
                    const n = Number(value);
                    return Number.isFinite(n) ? n.toFixed(2) : '';
                  }}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>

              <Typography
                variant="h5"
                align="center"
                sx={{ mt: 2, fontWeight: 'bold' }}
              >
                Projected RAF:{' '}
                {Number.isFinite(totalFuture)
                  ? totalFuture.toFixed(2)
                  : '0.00'}
              </Typography>

              {Number.isFinite(delta) && (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mt: 1 }}
                  color={delta >= 0 ? 'success.main' : 'error.main'}
                >
                  Change vs current RAF:&nbsp;
                  <strong>
                    {delta >= 0 ? '+' : ''}
                    {delta.toFixed(2)}
                  </strong>
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de información */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assumptions for Future RAF Score</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            The future RAF score is a projection based on all HCC codes
            suggested by the AI, assuming that:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                All clearly documented active conditions are coded (currentConditions).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Additional possible or weakly documented chronic conditions
                identified by the AI are correctly documented and captured
                (futureConditions).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                RAF weights come from the same HCC model and configuration
                used for the current RAF score.
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FutureRAFScore;
